import { useQuery } from '@tanstack/react-query';

const SHEET_ID =
  process.env.REACT_APP_AD_BANNER_SHEET_ID || '1mrwPZxfZk1oI50KJyZjKa6a-SmQa81RlcQX9Y0nWlKM';
const SHEET_NAME = process.env.REACT_APP_AD_BANNER_SHEET_NAME || 'ชีต1';

const BANNER_KEYS = ['banner_1', 'banner_2', 'banner_3'];
const FALLBACK_COLUMNS = {
  banner_1: 2,
  banner_2: 3,
  banner_3: 4,
};

export function extractGoogleDriveFileId(url) {
  if (!url || typeof url !== 'string') return '';

  const trimmed = url.trim();
  const patterns = [
    /drive\.google\.com\/file\/d\/([^/]+)/,
    /drive\.google\.com\/open\?id=([^&]+)/,
    /drive\.google\.com\/uc\?(?:export=(?:view|download)&)?id=([^&]+)/,
    /[?&]id=([^&]+)/,
  ];

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match?.[1]) return match[1];
  }

  return '';
}

export function getGoogleDriveImageCandidates(fileId) {
  if (!fileId) return [];

  return [
    `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`,
    `https://drive.google.com/uc?export=view&id=${fileId}`,
    `https://lh3.googleusercontent.com/d/${fileId}=w2000`,
  ];
}

export function toDisplayImageUrl(url) {
  if (!url || typeof url !== 'string') return '';

  const trimmed = url.trim();
  if (!trimmed) return '';

  const fileId = extractGoogleDriveFileId(trimmed);
  if (fileId) {
    return getGoogleDriveImageCandidates(fileId)[0];
  }

  return trimmed;
}

function parseGvizResponse(rawText) {
  const jsonStart = rawText.indexOf('{');
  const jsonEnd = rawText.lastIndexOf('}');
  if (jsonStart === -1 || jsonEnd === -1) {
    throw new Error('Invalid Google Sheets response');
  }

  const json = JSON.parse(rawText.slice(jsonStart, jsonEnd + 1));
  const cols = json.table?.cols || [];
  const rows = json.table?.rows || [];

  const columnIndexByBanner = {};
  BANNER_KEYS.forEach((bannerKey) => {
    const headerIndex = cols.findIndex((col) => col.label === bannerKey);
    columnIndexByBanner[bannerKey] = headerIndex >= 0 ? headerIndex : FALLBACK_COLUMNS[bannerKey];
  });

  const banners = {
    banner_1: [],
    banner_2: [],
    banner_3: [],
  };

  rows.forEach((row) => {
    BANNER_KEYS.forEach((bannerKey) => {
      const colIndex = columnIndexByBanner[bannerKey];
      const cell = row.c?.[colIndex];
      const rawValue = String(cell?.v ?? cell?.f ?? '').trim();
      if (!rawValue) return;

      const imageUrl = toDisplayImageUrl(rawValue);
      if (imageUrl) {
        banners[bannerKey].push(imageUrl);
      }
    });
  });

  return banners;
}

async function fetchAdBanners() {
  const sheetParam = encodeURIComponent(SHEET_NAME);
  const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${sheetParam}&headers=1`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const text = await response.text();
  return parseGvizResponse(text);
}

export function useAdBanners() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['ad-banners', SHEET_ID, SHEET_NAME],
    queryFn: fetchAdBanners,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    banners: data ?? { banner_1: [], banner_2: [], banner_3: [] },
    loading: isLoading,
    fetching: isFetching,
    error: error?.message ?? null,
    reload: refetch,
  };
}
